'use client';

import React, { useEffect, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import { traceAction } from '@/feedback/actionBreadcrumb';
import { FEEDBACK_ACTION, FEEDBACK_SOURCE, type FeedbackSource } from '@/feedback/ontology/types';
import type { DisplayConceptRef, DisplayUser } from '@/lib/api';
import { updateFollows } from '@/lib/api';

import { Modal } from './Modal';
import { TopicSelector } from './TopicSelector';

/** Controls how long direct-follow success feedback stays visible before callbacks run. */
export const DIRECT_FOLLOW_SUCCESS_DISPLAY_MS = 500;

/** Tracks the direct-follow button feedback lifecycle for no-topic prompt flows. */
type DirectFollowState = 'idle' | 'submitting' | 'success';

interface FollowButtonProps {
  authUsername: string;
  followUsername: string;
  currentlyFollowing: boolean;
  topics: DisplayConceptRef[];
  onFollowUpdate?: (updatedUserInfo: DisplayUser) => void;
  /** Runs after direct follow success feedback finishes so parents can animate row removal. */
  onDirectFollowFeedbackComplete?: (updatedUserInfo: DisplayUser) => void;
  noTopicPrompt?: boolean;
}

export function FollowButton({
  authUsername,
  followUsername,
  currentlyFollowing,
  topics,
  onFollowUpdate,
  onDirectFollowFeedbackComplete,
  noTopicPrompt,
}: FollowButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState<DisplayConceptRef[]>(topics);
  const [directFollowState, setDirectFollowState] = useState<DirectFollowState>('idle');
  /** Tracks the pending direct-follow success timer so unmount cleanup can cancel it. */
  const directFollowFeedbackTimeoutIdRef = useRef<number | null>(null);
  /** Stores the pending timer resolver so unmount cleanup can unblock the async flow. */
  const directFollowFeedbackResolveRef = useRef<((didComplete: boolean) => void) | null>(null);
  /** Reports whether the component is still mounted before resuming direct-follow updates. */
  const isMountedRef = useRef(true);

  const isDirectFollowFlow = Boolean(noTopicPrompt) && !currentlyFollowing;
  const isDirectFollowBusy = directFollowState !== 'idle';

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (directFollowFeedbackTimeoutIdRef.current !== null) {
        window.clearTimeout(directFollowFeedbackTimeoutIdRef.current);
        directFollowFeedbackTimeoutIdRef.current = null;
      }

      directFollowFeedbackResolveRef.current?.(false);
      directFollowFeedbackResolveRef.current = null;
    };
  }, []);

  /** Waits for the direct-follow success label unless unmount cleanup cancels the delay. */
  const waitForDirectFollowFeedback = async (): Promise<boolean> => {
    return await new Promise<boolean>((resolve) => {
      directFollowFeedbackResolveRef.current = resolve;
      directFollowFeedbackTimeoutIdRef.current = window.setTimeout(() => {
        directFollowFeedbackTimeoutIdRef.current = null;
        directFollowFeedbackResolveRef.current = null;
        resolve(true);
      }, DIRECT_FOLLOW_SUCCESS_DISPLAY_MS);
    });
  };

  /** Shows brief direct-follow success feedback before letting parents update surrounding UI. */
  const completeDirectFollowFeedback = async (updatedUserInfo: DisplayUser): Promise<void> => {
    setDirectFollowState('success');
    const didCompleteFeedback = await waitForDirectFollowFeedback();

    if (!didCompleteFeedback || !isMountedRef.current) {
      return;
    }

    if (onDirectFollowFeedbackComplete) {
      onDirectFollowFeedbackComplete(updatedUserInfo);
    } else if (onFollowUpdate) {
      onFollowUpdate(updatedUserInfo);
    }

    if (!isMountedRef.current) {
      return;
    }

    setDirectFollowState('idle');
  };

  const handleOpenModal = () => {
    setShowModal(true);
    setSelectedTopics(topics);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTopics([]);
  };

  const handleFollowUnfollowUser = async () => {
    if (isDirectFollowBusy) {
      return;
    }

    if (isDirectFollowFlow) {
      setDirectFollowState('submitting');
    }

    try {
      // Feedback breadcrumb: record the user-follow attempt and its success/failure outcome.
      const updatedUserInfo: DisplayUser = await traceAction(
        currentlyFollowing ? FEEDBACK_ACTION.UNFOLLOW_USER : FEEDBACK_ACTION.FOLLOW_USER,
        () =>
          updateFollows({
            action: currentlyFollowing ? 'unfollow' : 'follow',
            username_to_follow: followUsername,
            topics: currentlyFollowing ? [] : selectedTopics.map((topic) => topic.cref),
          }),
        {
          // `noTopicPrompt` is the actual invocation-path signal: when set, the
          // button submits directly (no modal); otherwise submission comes from
          // the modal's confirm button. This holds for both follow and unfollow,
          // unlike `isDirectFollowFlow` which excludes the direct-unfollow case.
          source: noTopicPrompt
            ? FEEDBACK_SOURCE.FOLLOW_BUTTON_DIRECT
            : FEEDBACK_SOURCE.FOLLOW_BUTTON_MODAL,
        } satisfies { source: FeedbackSource }
      );

      if (isDirectFollowFlow) {
        await completeDirectFollowFeedback(updatedUserInfo);
      } else if (onFollowUpdate) {
        onFollowUpdate(updatedUserInfo);
      }

      handleCloseModal();
    } catch (error) {
      if (isDirectFollowFlow) {
        setDirectFollowState('idle');
      }
      console.error('Error following/unfollowing user:', error);
    }
  };

  const updateFollowUserUpdateTopics = async () => {
    try {
      const updatedUserInfo: DisplayUser = await updateFollows({
        action: 'update',
        username_to_follow: followUsername,
        topics: selectedTopics.map((topic) => topic.cref),
      });
      if (onFollowUpdate) {
        onFollowUpdate(updatedUserInfo);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error updating follow of user:', error);
    }
  };

  const handleTopicSelect = (topic: DisplayConceptRef | null) => {
    if (topic) {
      setSelectedTopics((prevTopics) => [...prevTopics, topic]);
    }
  };

  const buttonLabel = isDirectFollowFlow
    ? directFollowState === 'submitting'
      ? 'Following…'
      : directFollowState === 'success'
        ? 'Followed'
        : 'Follow'
    : currentlyFollowing
      ? 'Following'
      : 'Follow';
  const buttonColor =
    directFollowState === 'success' ? 'success' : currentlyFollowing ? 'inherit' : 'primary';

  return (
    <>
      <Button
        onClick={noTopicPrompt ? handleFollowUnfollowUser : handleOpenModal}
        disabled={isDirectFollowBusy}
        aria-haspopup={!noTopicPrompt ? 'dialog' : undefined}
        aria-expanded={showModal}
        variant={currentlyFollowing ? 'outlined' : 'contained'}
        color={buttonColor}
        size="small"
      >
        {buttonLabel}
      </Button>
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={`${currentlyFollowing ? 'unfollow' : 'follow'} ${followUsername} ${
          currentlyFollowing ? 'or update topics:' : 'on:'
        }`}
      >
        <Box sx={{ p: 2 }}>
          {selectedTopics.length === 0 ? (
            <List>
              <ListItem>
                <ListItemText primary={<Typography fontStyle="italic">All topics</Typography>} />
              </ListItem>
            </List>
          ) : (
            <Box sx={{ mb: 2 }}>
              <List>
                {selectedTopics.map((topic, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() =>
                          setSelectedTopics((prevTopics) =>
                            prevTopics.filter((_, i) => i !== index)
                          )
                        }
                        aria-label={`Remove topic ${topic.display}`}
                        size="small"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={topic.display} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography component="label" htmlFor="topic-selector" sx={{ display: 'block', mb: 1 }}>
              {selectedTopics.length === 0 ? 'Or select topics:' : 'Add topic:'}
            </Typography>
            <TopicSelector
              instanceId="follow-button-topic-selector"
              onChangeFn={handleTopicSelect}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {currentlyFollowing && (
              <Button onClick={updateFollowUserUpdateTopics} variant="contained">
                Update Topics
              </Button>
            )}
            <Button
              onClick={handleFollowUnfollowUser}
              variant="contained"
              color={currentlyFollowing ? 'error' : 'primary'}
            >
              {currentlyFollowing ? 'unfollow' : 'follow'}
            </Button>
            <Button onClick={handleCloseModal} variant="outlined">
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
